import React, { useContext } from "react";
import { Stack, Typography, Badge, Divider } from "@mui/material";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import AddIcon from "@mui/icons-material/Add";
import RemoveOutlinedIcon from "@mui/icons-material/RemoveOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import DocumentScannerOutlinedIcon from "@mui/icons-material/DocumentScannerOutlined";
import { useTheme } from "@mui/material/styles";
import { UserContext } from "../../../UserProvider";
import { UserEventType } from "../../../constants";
import { useI18n } from "../../../hooks/useI18n";

const eventOrder = [
  "StartScanning",
  "ProductScanToCart",
  "ProductManuallyAddToCart",
  "ProductRemovedFromCart",
  "CartEmpty"
];

function CartActions({ actions }) {
  const theme = useTheme();
  const user = useContext(UserContext);
  const primaryColor =
    user.tenant.configuration?.primaryColor || theme.palette.primary.main;
  const { t } = useI18n();

  const sorted = actions.sort(
    (a, b) => eventOrder.indexOf(b.event) - eventOrder.indexOf(a.event)
  );

  const groupedActions = sorted.reduce((acc, action) => {
    const itemName =
      action.dynamicData.eventTenantItem?.tenantItemName ||
      t("common.messages.unknownItem");
    const key = `${action.event}-${itemName}`;
    if (!acc[key]) {
      acc[key] = { ...action, count: 1 };
    } else {
      acc[key].count += 1;
    }
    return acc;
  }, {});

  return (
    <Stack spacing={1.5}>
      {Object.values(groupedActions).map((action, index) => {
        const itemName = action.dynamicData.eventTenantItem?.tenantItemName;

        return (
          <Stack
            key={index}
            direction="row"
            spacing={1.5}
            alignItems="center"
            sx={{
              py: 0.75,
              opacity: action.successful ? 1 : 0.6,
              borderBottom:
                index < Object.values(groupedActions).length - 1
                  ? `1px solid ${theme.palette.divider}`
                  : "none"
            }}
          >
            {action.event === UserEventType.SCANNER_START_SCANNING && (
              <DocumentScannerOutlinedIcon
                sx={{
                  color: primaryColor,
                  transform: "rotate(90deg)"
                }}
              />
            )}

            {action.event === UserEventType.CART_EMPTY && (
              <DeleteOutlinedIcon sx={{ color: theme.palette.error.main }} />
            )}

            {action.event === UserEventType.CART_DECREASE && (
              <RemoveOutlinedIcon sx={{ color: theme.palette.warning.main }} />
            )}

            {action.event === UserEventType.CART_INCREASE && (
              <Badge
                color="success"
                variant="dot"
                invisible={!action.successful}
              >
                <AddIcon sx={{ color: primaryColor }} />
              </Badge>
            )}

            {action.event === UserEventType.SCANNER_ON_SCAN_FOUND && (
              <DocumentScannerOutlinedIcon
                sx={{
                  color: primaryColor,
                  transform: "rotate(90deg)"
                }}
              />
            )}

            <Stack sx={{ flexGrow: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {action.event === UserEventType.SCANNER_START_SCANNING &&
                  t("common.messages.scannerStarted")}
                {action.event === UserEventType.CART_EMPTY &&
                  t("common.messages.cartEmptied")}
                {action.event === UserEventType.CART_DECREASE && (
                  <>
                    {t("common.messages.removed")}:{" "}
                    <span style={{ fontWeight: 600 }}>{itemName}</span>
                  </>
                )}
                {action.event === UserEventType.CART_INCREASE && (
                  <>
                    {t("common.messages.added")}:{" "}
                    <span style={{ fontWeight: 600, color: primaryColor }}>
                      {itemName}
                    </span>
                  </>
                )}
                {action.event === UserEventType.SCANNER_ON_SCAN_FOUND && (
                  <>
                    {t("common.messages.scanned")}:{" "}
                    <span style={{ fontWeight: 600, color: primaryColor }}>
                      {itemName}
                    </span>
                  </>
                )}
              </Typography>
            </Stack>

            {
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.text.secondary,
                  ml: "auto"
                }}
              >
                ×{action.count}
              </Typography>
            }
          </Stack>
        );
      })}
    </Stack>
  );
}

export default CartActions;
